import { reactive } from 'vue'

const state = reactive({
    alert: {
        show: false,
        title: '',
        message: '',
        type: 'info', // info, success, warning, error
        confirmText: 'OK',
        onConfirm: null
    },
    confirm: {
        show: false,
        title: '',
        message: '',
        type: 'warning',
        confirmText: 'Confirmer',
        cancelText: 'Annuler',
        showToggle: false,
        toggleLabel: '',
        toggleValue: false,
        onConfirm: null,
        onCancel: null
    },
    prompt: {
        show: false,
        title: '',
        message: '',
        value: '',
        placeholder: '',
        type: 'info',
        confirmText: 'Confirmer',
        cancelText: 'Annuler',
        onConfirm: null,
        onCancel: null
    }
})

export function useDialog() {
    // Alert personnalisée
    const showAlert = (options, maybeTypeOrTitle = null) => {
        return new Promise((resolve) => {
            if (typeof options === 'string') {
                const isType = ['info', 'success', 'warning', 'error'].includes(maybeTypeOrTitle)
                options = { 
                    message: options,
                    type: isType ? maybeTypeOrTitle : 'info',
                    title: !isType && maybeTypeOrTitle ? maybeTypeOrTitle : (isType ? (maybeTypeOrTitle === 'success' ? 'Succès' : maybeTypeOrTitle === 'error' ? 'Erreur' : 'Information') : 'Information')
                }
            }

            state.alert = {
                show: true,
                title: options.title || 'Information',
                message: options.message || '',
                type: options.type || 'info',
                confirmText: options.confirmText || 'OK',
                onConfirm: () => {
                    state.alert.show = false
                    resolve(true)
                }
            }
        })
    }

    // Confirm personnalisée
    const showConfirm = (options, maybeTitle = null) => {
        return new Promise((resolve) => {
            if (typeof options === 'string') {
                options = { 
                    message: options,
                    title: maybeTitle || 'Confirmation'
                }
            }

            state.confirm = {
                show: true,
                title: options.title || 'Confirmation',
                message: options.message || '',
                type: options.type || 'warning',
                confirmText: options.confirmText || 'Confirmer',
                cancelText: options.cancelText || 'Annuler',
                showToggle: options.showToggle || false,
                toggleLabel: options.toggleLabel || '',
                toggleValue: options.toggleValue || false,
                onConfirm: () => {
                    state.confirm.show = false
                    resolve(state.confirm.showToggle ? { confirmed: true, toggleValue: state.confirm.toggleValue } : true)
                },
                onCancel: () => {
                    state.confirm.show = false
                    resolve(state.confirm.showToggle ? { confirmed: false, toggleValue: false } : false)
                }
            }
        })
    }

    // Prompt personnalisée
    const showPrompt = (options) => {
        return new Promise((resolve) => {
            if (typeof options === 'string') {
                options = { message: options }
            }

            state.prompt = {
                show: true,
                title: options.title || 'Saisie requise',
                message: options.message || '',
                value: options.value || '',
                placeholder: options.placeholder || '',
                type: options.type || 'info',
                confirmText: options.confirmText || 'Confirmer',
                cancelText: options.cancelText || 'Annuler',
                onConfirm: (value) => {
                    state.prompt.show = false
                    resolve(value)
                },
                onCancel: () => {
                    state.prompt.show = false
                    resolve(null)
                }
            }
        })
    }

    // Raccourcis pour différents types
    const alert = {
        info: (message, title = 'Information') => showAlert({ message, title, type: 'info' }),
        success: (message, title = 'Succès') => showAlert({ message, title, type: 'success' }),
        warning: (message, title = 'Attention') => showAlert({ message, title, type: 'warning' }),
        error: (message, title = 'Erreur') => showAlert({ message, title, type: 'error' })
    }

    const confirm = {
        delete: (itemName) => showConfirm({
            title: 'Confirmer la suppression',
            message: `Êtes-vous sûr de vouloir supprimer "${itemName}" ? Cette action est irréversible.`,
            type: 'error',
            confirmText: 'Supprimer',
            cancelText: 'Annuler'
        }),
        action: (message, title = 'Confirmation') => showConfirm({
            title,
            message,
            type: 'warning',
            confirmText: 'Confirmer',
            cancelText: 'Annuler'
        })
    }

    const prompt = {
        input: (message, title = 'Saisie requise', placeholder = '', value = '') => showPrompt({
            message,
            title,
            placeholder,
            value,
            type: 'info'
        })
    }

    return {
        state,
        showAlert,
        showConfirm,
        showPrompt,
        alert,
        confirm,
        prompt
    }
}
